import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucideUserPlus } from "lucide-react";
import { Modal } from "@/components/modals/Modal";

/**
 * Two-column modal for adding a new student report card to a Report Folder.
 * Left: input form. Right: scrollable list of students already in the Report Folder.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(studentName: string, oen: string, applyDefaults: boolean) => Promise<void>} props.onSubmit
 *   Called with trimmed values; should throw with a message string on failure.
 * @param {boolean} props.hasDefaults - Whether the Report Folder has report defaults configured
 * @param {Array} props.existingReports - Reports already in this Report Folder (for the reference list)
 * @param {boolean} props.loadingReports - Show skeleton while reports are being fetched
 */
export function AddStudentModal({
  open,
  onClose,
  onSubmit,
  hasDefaults = false,
  existingReports = [],
  loadingReports = false,
}) {
  const [studentName, setStudentName] = useState("");
  const [studentOen, setStudentOen] = useState("");
  const [applyDefaults, setApplyDefaults] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  function reset() {
    setStudentName("");
    setStudentOen("");
    setApplyDefaults(true);
    setCreateError("");
  }

  function handleClose() {
    if (creating) return;
    reset();
    onClose();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!studentName.trim()) return;
    setCreating(true);
    setCreateError("");
    try {
      await onSubmit(studentName.trim(), studentOen.trim(), applyDefaults);
      // parent navigates on success - no reset needed
    } catch (err) {
      setCreateError(err.message || "Failed to create report card. Please try again.");
      setCreating(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Student"
      subtitle="Create a new report card for a student in this Report Folder."
      icon={<LucideUserPlus size={16} className="text-primary" />}
      maxWidth="max-w-2xl"
      maxHeight="max-h-[80vh]"
      preventClose={creating}
    >
      <div className="flex flex-1 min-h-0">
        {/* Left: form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 px-6 py-5 space-y-4 overflow-y-auto border-r border-border"
        >
          <div className="space-y-1.5">
            <Label>
              Student Name{" "}
              <span className="text-destructive/80 font-normal">*</span>
            </Label>
            <Input
              placeholder="e.g. Jane Smith"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label>
              OEN{" "}
              <span className="text-muted-foreground font-normal text-xs">
                (optional)
              </span>
            </Label>
            <Input
              placeholder="e.g. 123456789"
              value={studentOen}
              onChange={(e) => setStudentOen(e.target.value)}
            />
          </div>

          {hasDefaults && (
            <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={applyDefaults}
                onChange={(e) => setApplyDefaults(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span className="text-sm text-foreground">
                Apply Report Folder defaults to this report
              </span>
            </label>
          )}

          {createError && (
            <p className="text-sm text-destructive">{createError}</p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={creating || !studentName.trim()}
            >
              {creating ? "Creating..." : "Create Report Card"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={creating}
            >
              Cancel
            </Button>
          </div>
        </form>

        {/* Right: existing students reference list */}
        <div className="w-56 shrink-0 px-4 py-5 overflow-y-auto">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Students in Report Folder
          </p>

          {loadingReports ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-10 bg-muted rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : existingReports.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No students added yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {existingReports.map((r) => (
                <li
                  key={r._id}
                  className="rounded-lg px-3 py-2 bg-muted/40 flex items-center justify-between gap-2"
                >
                  <p className="text-sm font-medium text-foreground leading-tight truncate">
                    {r.studentName}
                  </p>
                  {r.data?.oen && (
                    <p className="text-sm text-muted-foreground tabular-nums shrink-0">
                      {r.data.oen}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
